<template>
  <Teleport to="body">
  <div
    v-if="visible"
    class="pointer-events-none fixed inset-0 z-(--z-modal) flex items-center justify-center p-6
           backdrop-blur-xs"
  >
  <Popup
    class="pointer-events-auto [&_.small-popup]:static! [&_.small-popup]:max-h-[90vh] [&_.small-popup]:w-[min(85vw,52rem)]
           [&_.small-popup]:overflow-auto [&_.small-popup]:rounded-card! [&_.small-popup]:border
           [&_.small-popup]:border-line [&_.small-popup]:bg-popover-solid! [&_.small-popup]:p-5!
           [&_.small-popup]:shadow-(--shadow-modal)! [&_.small-popup]:[transform:none]! max-sm:[&_.small-popup]:p-4!"
    @click.stop
    @close="onClose"
  >
    <div class="flex flex-col gap-5">
      <div
        ref="pickerLayer"
        class="pointer-events-none fixed inset-0 z-[calc(var(--z-modal)+1)]"
      ></div>

      <div class="flex items-start justify-between gap-6 border-b border-line pb-5">
        <div class="flex flex-col gap-2.5">
          <div class="flex items-baseline gap-3">
            <h2 class="m-0 text-title font-bold tracking-[-0.01em] text-text">ETA</h2>
            <span class="text-label text-text-muted">{{ sprintRangeLabel }}</span>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-unit font-medium uppercase tracking-[0.09em] text-text-muted">{{ $t('projectSettings.tag') }}</span>
            <span
              class="flex max-w-65 items-center gap-1.75 rounded-chip border border-accent/30
                     bg-accent-soft px-2.5 py-1 text-label text-accent"
            >
              <span class="size-1.5 shrink-0 rounded-full bg-accent"></span>
              <span class="truncate" :title="tag?.name">{{ tag?.name }}</span>
            </span>
          </div>
        </div>
        <button
          type="button"
          class="flex size-8 shrink-0 cursor-pointer items-center justify-center
                 rounded-chip border-0 bg-transparent p-0 text-muted-soft transition-colors
                 duration-150 hover:bg-hover hover:text-accent"
          @click="onClose"
        >
          <IconXMark16Solid class="size-4 shrink-0" />
        </button>
      </div>

      <div
        class="grid grid-cols-3 gap-3
               [&>div]:flex [&>div]:flex-col [&>div]:gap-1.5 [&>div]:rounded-card [&>div]:border
               [&>div]:border-line [&>div]:bg-input [&>div]:px-4 [&>div]:py-3.5
               [&>div>span]:text-unit [&>div>span]:font-medium [&>div>span]:uppercase
               [&>div>span]:tracking-[0.09em] [&>div>span]:text-text-muted
               [&_p]:m-0 [&_p]:flex [&_p]:items-baseline [&_p]:gap-1.5 [&_p]:text-lg
               [&_p]:leading-none [&_p]:text-text
               [&_p>span]:text-lg [&_p>span]:font-normal [&_p>span]:text-text-muted"
      >
        <div>
          <span>{{ $t('taskFilters.inTotal') }}</span>
          <p>
            {{ calculatedETA.result.passedTasks }}
            <span>/ {{ calculatedETA.result.totalTasks }}</span>
          </p>
        </div>
        <div>
          <span>{{ $t('taskFilters.estimation') }}</span>
          <p>
            {{ calculatedETA.result.totalEstimate }}
            <span>h</span>
          </p>
        </div>
        <div>
          <span>{{ $t('tags.burned_hours') }}</span>
          <p>
            {{ toHours(calculatedETA.result.totalTracked_sec) }}
            <span>h</span>
            <span :class="progressTone(calculatedETA.result.progress, BURN_TONE)">
              {{ percent(calculatedETA.result.progress) }}
            </span>
          </p>
        </div>
      </div>

      <div
        class="overflow-x-auto rounded-card border border-line text-text
               [&_th]:whitespace-nowrap [&_th]:border-b [&_th]:border-line [&_th]:bg-input
               [&_th]:px-3 [&_th]:py-2.5 [&_th]:text-right [&_th]:text-unit [&_th]:font-medium
               [&_th]:uppercase [&_th]:tracking-[0.09em] [&_th]:text-text-muted
               [&_td]:whitespace-nowrap [&_td]:border-b [&_td]:border-line [&_td]:px-3 [&_td]:py-2.5
               [&_td]:text-right [&_td]:tabular-nums [&_td]:text-label
               [&_th:first-child]:text-left
               [&_td:first-child]:text-left [&_td:first-child]:[font-family:inherit]"
      >
        <table v-if="mode !== 'velocity'" class="w-full border-collapse">
          <thead>
            <tr>
              <th>{{ $t('taskFilters.assignee') }}</th>
              <th>{{ $t('tags.planned') }}</th>
              <th>{{ $t('tags.passed') }}</th>
              <th>{{ $t('tags.est_h') }}</th>
              <th>{{ $t('tags.tracked') }}</th>
              <th>{{ $t('tags.burned') }}</th>
              <th>{{ $t('tags.avg_velocity') }}</th>
              <th>ETA</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="userEta in calculatedETA.ETAsByAssignees" :key="userEta.userData.id">
              <td>
                <div class="flex items-center gap-2.5">
                  <UserAvatar withDayoffBadge :user="userEta.userData" class="size-6! shrink-0" />
                  <span class="truncate font-medium">{{ userEta.userData.username }}</span>
                </div>
              </td>
              <td>{{ userEta.totalTasks }}</td>
              <td :class="progressTone(userEta.taskProgress, TASK_TONE)">
                {{ userEta.passedTasks }} ({{ Math.round(userEta.passedTasks / userEta.totalTasks * 100) }}%)
              </td>
              <td>{{ userEta.totalEstimate }}</td>
              <td><SpentTime :seconds="userEta.alreadyTrackedByTag_sec" /></td>
              <td :class="progressTone(userEta.progress, BURN_TONE)">{{ percent(userEta.progress) }}</td>
              <td><SpentTime :seconds="userEta.velStats.beforeSprint.allProjects" /></td>
              <td>{{ userEta.ETA }}</td>
            </tr>
            <tr class="bg-input [&>td]:border-b-0 [&>td]:font-medium">
              <td>{{ $t('taskFilters.inTotal') }}</td>
              <td>{{ calculatedETA.result.totalTasks }}</td>
              <td :class="progressTone(calculatedETA.result.taskProgress, TASK_TONE)">
                {{ calculatedETA.result.passedTasks }} ({{ calculatedETA.result.taskProgress }}%)
              </td>
              <td>{{ calculatedETA.result.totalEstimate }}</td>
              <td><SpentTime :seconds="calculatedETA.result.totalTracked_sec" /></td>
              <td :class="progressTone(calculatedETA.result.progress, BURN_TONE)">{{ percent(calculatedETA.result.progress) }}</td>
              <td>—</td>
              <td>{{ calculatedETA.result.ETA }}</td>
            </tr>
          </tbody>
        </table>

        <table v-else class="w-full border-collapse">
          <thead>
            <tr class="[&>th]:text-center!">
              <th rowspan="2">{{ $t('taskFilters.assignee') }}</th>
              <th class="border-l">{{ $t('tags.before_sprint') }}</th>
              <th colspan="4" class="border-l">{{ $t('tags.during_sprint') }}</th>
              <th colspan="3" class="border-l">KPIs</th>
            </tr>
            <tr
              class="[&>th:nth-child(1)]:border-l [&>th:nth-child(2)]:border-l
                     [&>th:nth-child(6)]:border-l"
            >
              <th>{{ $t('tags.all_projects') }}</th>
              <th>{{ $t('tags.all_projects') }}</th>
              <th>{{ $t('tags.all_sprint_tasks') }}</th>
              <th>{{ $t('tags.non_own_sprint_tasks') }}</th>
              <th>{{ $t('tags.non_this_project') }}</th>
              <th>{{ $t('tags.effort_change') }}</th>
              <th>{{ $t('tags.native_level') }}</th>
              <th>{{ $t('tags.altruist') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="userEta in calculatedETA.ETAsByAssignees"
              :key="userEta.userData.id"
              class="[&>td:nth-child(2)]:border-l [&>td:nth-child(3)]:border-l
                     [&>td:nth-child(7)]:border-l"
            >
              <td>
                <div class="flex items-center gap-2.5">
                  <UserAvatar withDayoffBadge :user="userEta.userData" class="size-6! shrink-0" />
                  <span class="truncate font-medium">{{ userEta.userData.username }}</span>
                </div>
              </td>
              <td><SpentTime :seconds="userEta.velStats.beforeSprint.allProjects" /></td>
              <td><SpentTime :seconds="userEta.velStats.duringSprint.allProjects" /></td>
              <td><SpentTime :seconds="userEta.velStats.duringSprint.sprintAllTasks" /></td>
              <td><SpentTime :seconds="userEta.velStats.duringSprint.sprintNonOwnTasks" /></td>
              <td><SpentTime :seconds="userEta.velStats.duringSprint.allProjects - userEta.velStats.duringSprint.sameProject" /></td>
              <td :class="wordTone(userEta.velStats.kpis.effortInWord === 'increased', userEta.velStats.kpis.effortInWord === 'decreased')">
                {{ Math.round(userEta.velStats.kpis.effortChangeInPercent) }} ({{ userEta.velStats.kpis.effortInWord }})
              </td>
              <td :class="wordTone(userEta.velStats.kpis.alien < 10, userEta.velStats.kpis.alien > 10)">
                {{ 100 - Math.round(userEta.velStats.kpis.alien) }}%
              </td>
              <td :class="wordTone(userEta.velStats.kpis.altruist < 50, userEta.velStats.kpis.altruist > 50)">
                {{ Math.round(userEta.velStats.kpis.altruist) }}%
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        v-if="sprintSettings"
        class="flex flex-wrap items-end gap-5 rounded-card border border-line bg-input px-4 py-3.5
               [&_label]:text-unit [&_label]:font-medium [&_label]:uppercase
               [&_label]:tracking-[0.09em] [&_label]:text-text-muted"
      >
        <div class="flex flex-col gap-1.5">
          <label for="sprintStart">{{ $t('tags.sprint_start') }}</label>
          <DatePicker
            id="sprintStart"
            class="w-72"
            :model-value="sprintSettings.startDate"
            @update:model-value="setSprintStartDate"
            :model-type="'iso'"
            format="MMM dd, yyyy"
            :teleport="pickerLayer"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label for="weekendsPerWeek">{{ $t('tags.weekends_per_week') }}</label>
          <SingleLineInput id="weekendsPerWeek" class="w-0 min-w-full" type="number" v-model="sprintSettings.weekendsPerWeek" min="1" max="100" />
        </div>
        <div class="flex flex-col gap-1.5">
          <label for="daysInSprint">{{ $t('tags.days_in_sprint') }}</label>
          <SingleLineInput id="daysInSprint" class="w-0 min-w-full" type="number" v-model="sprintSettings.daysInSprint" min="1" max="100" />
        </div>
        <div class="flex-1"></div>
        <SimpleButton
          size="sm"
          :text="mode === 'main' ? $t('tags.show_velocity_stats') : $t('tags.show_main_stats')"
          @click="mode = mode === 'main' ? 'velocity' : 'main'"
        />
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex items-baseline justify-between gap-4">
          <div class="flex flex-col gap-0.75">
            <h3 class="m-0 text-body font-bold text-text">{{ $t('tags.burndown') }}</h3>
            <span class="text-label text-text-muted">{{ $t('tags.remaining_vs_ideal_pace') }}</span>
          </div>
          <div class="flex items-center gap-4.5 [&_i]:h-0.75 [&_i]:w-4.5 [&_i]:rounded-sm">
            <span class="inline-flex items-center gap-2 text-label text-text-soft">
              <i class="bg-accent"></i>{{ $t('tags.remaining') }}
            </span>
            <span class="inline-flex items-center gap-2 text-label text-text-soft">
              <i class="bg-cyan"></i>{{ $t('tags.ideal') }}
            </span>
          </div>
        </div>
        <LineChartGenerator :data="chartData" :options="chartOptions" :height="100" />
      </div>

    </div>
  </Popup>
  </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import dayjs from 'dayjs';
import { Line as LineChartGenerator } from 'vue-chartjs';
import DatePicker from '@/components/elements/DatePicker.vue';
import SingleLineInput from '@/components/elements/SingleLineInput.vue';
import SpentTime from '@/components/elements/SpentTime.vue';
import { IconXMark16Solid } from '@iconify-prerendered/vue-heroicons';

const TASK_TONE = { green: v => v > 90, amber: v => v >= 80 && v < 90 };
const BURN_TONE = { green: v => v < 80, amber: v => v >= 80 && v < 100 };

const progressTone = (value, tone) => {
  if (!Number.isFinite(value)) return '';
  if (tone.green(value)) return 'text-green';
  if (tone.amber(value)) return 'text-amber';
  return 'text-red';
};

const wordTone = (isGood, isBad) => (isGood && 'text-green') || (isBad && 'text-red') || '';

const percent = value => (value === '-' ? '—' : `${value} %`);

const toHours = seconds => ((seconds || 0) / 3600).toFixed(1);

const themeColor = name => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

const pickerLayer = ref(null);

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const props = defineProps({
  open: { type: Boolean, default: false },
  tag: { type: Object, default: null },
  projectId: { type: [String, Number], required: true },
});

const emit = defineEmits(['update:open']);

const store = useStore();
const { t } = useI18n();

const mode = ref('main');
const sprintSettings = ref(null);
const sprintRangeLabel = computed(() => {
  const start = dayjs(sprintSettings.value?.startDate);
  const end = start.add(Number(sprintSettings.value?.daysInSprint || 1) - 1, 'day');
  return `${start.format('MMM DD')} – ${end.format('MMM DD, YYYY')}`;
});
const totalTrackedByDates = ref([]);
const calculatedETA = ref(null);
const syncSprintSettingsFromResponse = ref(false);


function getLocalDateFormatted(date) {
  return dayjs(date).format('MMM DD, YYYY');
}

function normalizeDateToUtcStartOfDay(value) {
  if (!value) return '';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return '';

  return new Date(Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  )).toISOString();
}

const getUser = store.getters['user/getUser'];

const visible = computed(() => props.open && !!calculatedETA.value);

function formatSprintSettingsForInput(settings) {
  if (!settings) return null;

  return {
    ...settings,
    startDate: normalizeDateToUtcStartOfDay(settings.startDate),
    weekendsPerWeek: settings.weekendsPerWeek === undefined || settings.weekendsPerWeek === null
      ? ''
      : `${settings.weekendsPerWeek}`,
    daysInSprint: settings.daysInSprint === undefined || settings.daysInSprint === null
      ? ''
      : `${settings.daysInSprint}`,
  };
}

function getSprintSettingsPayload(settings) {
  if (!settings) return null;

  return {
    ...settings,
    weekendsPerWeek: settings.weekendsPerWeek === '' ? undefined : Number(settings.weekendsPerWeek),
    daysInSprint: settings.daysInSprint === '' ? undefined : Number(settings.daysInSprint),
  };
}

function setSprintStartDate(value) {
  if (!sprintSettings.value) return;

  sprintSettings.value.startDate = normalizeDateToUtcStartOfDay(value);
}

function hasIncompleteSprintSettings(settings) {
  if (!settings) return true;

  return [settings.startDate, settings.weekendsPerWeek, settings.daysInSprint]
    .some((value) => value === '' || value === undefined || value === null);
}

async function calculateETA() {
  if (!props.tag?.id || !props.projectId) return;
  const { ETAByAssignee, result, totalTrackedByDates: tbd, sprintSettings: ss } = await store.dispatch('project/actionCalculateETAByTag', {
    tagId: props.tag.id,
    projectId: props.projectId,
    sprintSettings: getSprintSettingsPayload(sprintSettings.value),
  });
  syncSprintSettingsFromResponse.value = true;
  sprintSettings.value = formatSprintSettingsForInput(ss);
  await nextTick();
  syncSprintSettingsFromResponse.value = false;

  const ETAsByAssignees = Object.keys(ETAByAssignee).map((assigneeId) => ({
    ...ETAByAssignee[assigneeId],
    userData: getUser(assigneeId),
    progress:
      ETAByAssignee[assigneeId].totalEstimate === 0
        ? '-'
        : Math.round((ETAByAssignee[assigneeId].alreadyTrackedByTag_sec / 3600 / ETAByAssignee[assigneeId].totalEstimate) * 100),
    taskProgress:
      ETAByAssignee[assigneeId].totalEstimate === 0
        ? '-'
        : Math.round((ETAByAssignee[assigneeId].passedTasks / ETAByAssignee[assigneeId].totalTasks) * 100),
    ETA: ETAByAssignee[assigneeId].ETA ? getLocalDateFormatted(new Date(ETAByAssignee[assigneeId].ETA)) : '-',
  }));
  result.ETA = result.ETA ? getLocalDateFormatted(new Date(result.ETA)) : '-';
  result.progress = result.totalEstimate === 0 ? '-' : Math.round((result.totalTracked_sec / 3600 / result.totalEstimate) * 100);
  result.taskProgress = Math.round((result.passedTasks / result.totalTasks) * 100);

  calculatedETA.value = {
    ETAsByAssignees,
    result,
    tag: props.tag,
  };
  totalTrackedByDates.value = tbd;
}

function onClose() {
  emit('update:open', false);
  calculatedETA.value = null;
}

watch(
  () => props.open,
  (v) => {
    if (v && props.tag) calculateETA();
  },
  { immediate: false }
);

watch(
  () => [sprintSettings.value?.startDate, sprintSettings.value?.weekendsPerWeek, sprintSettings.value?.daysInSprint],
  (nv, ov) => {
    if (!ov || syncSprintSettingsFromResponse.value) return;
    if (nv.every((value, index) => value === ov[index])) return;
    if (hasIncompleteSprintSettings(sprintSettings.value)) return;
    if (props.open && props.tag) calculateETA();
  }
);

const remainingHours = computed(() => {
  const remainingH = [];
  let remaining = total_estimate.value;
  for (let i = 0; i < (sprintSettings.value?.daysInSprint || 0); i += 1) {
    if (totalTrackedByDates.value[i]) {
      remaining -= totalTrackedByDates.value[i].hours || 0;
      remainingH.push(remaining);
    }
  }
  return remainingH;
});

const total_estimate = computed(() => calculatedETA.value?.result.totalEstimate || 0);

const chartData = computed(() => ({
  labels: Array.from({ length: sprintSettings.value?.daysInSprint || 0 }, (_, i) =>
    totalTrackedByDates.value[0]?.date && getLocalDateFormatted(dayjs(totalTrackedByDates.value[0]?.date).add(i, 'day'))
  ),
  datasets: [
    {
      label: t('tags.remaining_hours'),
      borderColor: themeColor('--accent'),
      backgroundColor: `${themeColor('--accent')}1f`,
      data: remainingHours.value,
      fill: true,
      yAxisID: 'rHours',
    },
    {
      label: t('tags.ideal_burndown'),
      borderColor: themeColor('--cyan'),
      backgroundColor: `${themeColor('--cyan')}1f`,
      data: Array.from({ length: sprintSettings.value?.daysInSprint || 0 }, (_, i) =>
        total_estimate.value - (total_estimate.value / (sprintSettings.value?.daysInSprint || 1)) * (i + 1)
      ),
      fill: true,
      yAxisID: 'rHours',
    },
  ],
}));

const chartOptions = computed(() => ({
  plugins: {
    title: { display: false },
    legend: { display: false },
  },
  scales: {
    rHours: {
      type: 'linear',
      position: 'left',
      grid: { color: `${themeColor('--muted')}26` },
      ticks: { color: themeColor('--muted') },
    },
    x: {
      offset: true,
      grid: { color: `${themeColor('--muted')}1a` },
      ticks: { color: themeColor('--muted'), beginAtZero: true },
    },
  },
}));

// (no export logic here; ETA popup unchanged)

</script>

